import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  where,
  limit,
  serverTimestamp,
  Timestamp,
  writeBatch,
} from "firebase/firestore";
import { db } from "./firebase";
import { Participante, Confirmado, ParticipanteForm, OrigemConfirmado } from "@/types/participante";

// ─── Lista de Espera ─────────────────────────────────────────────────────────

export async function listarEspera(edicaoId: string): Promise<Participante[]> {
  const q = query(
    collection(db, "edicoes", edicaoId, "listaEspera"),
    orderBy("criadoEm", "asc")
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      nomeCompleto: data.nomeCompleto,
      cpf: data.cpf,
      email: data.email,
      whatsapp: data.whatsapp,
      criadoEm: (data.criadoEm as Timestamp)?.toDate() ?? new Date(),
    };
  });
}

export async function adicionarEspera(
  edicaoId: string,
  participante: ParticipanteForm
): Promise<string> {
  const existe = await getDocs(
    query(
      collection(db, "edicoes", edicaoId, "listaEspera"),
      where("cpf", "==", participante.cpf),
      limit(1)
    )
  );
  if (!existe.empty) {
    throw new Error("CPF já cadastrado na lista de espera.");
  }

  const ref = await addDoc(
    collection(db, "edicoes", edicaoId, "listaEspera"),
    { ...participante, criadoEm: serverTimestamp() }
  );
  return ref.id;
}

export async function removerEspera(edicaoId: string, participanteId: string): Promise<void> {
  await deleteDoc(doc(db, "edicoes", edicaoId, "listaEspera", participanteId));
}

// ─── Confirmados ─────────────────────────────────────────────────────────────

export async function listarConfirmados(edicaoId: string): Promise<Confirmado[]> {
  const q = query(
    collection(db, "edicoes", edicaoId, "confirmados"),
    orderBy("criadoEm", "asc")
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      nomeCompleto: data.nomeCompleto,
      cpf: data.cpf,
      email: data.email,
      whatsapp: data.whatsapp,
      origem: data.origem as OrigemConfirmado,
      criadoEm: (data.criadoEm as Timestamp)?.toDate() ?? new Date(),
    };
  });
}

export async function adicionarConfirmado(
  edicaoId: string,
  participante: ParticipanteForm,
  origem: OrigemConfirmado
): Promise<string> {
  const ref = await addDoc(
    collection(db, "edicoes", edicaoId, "confirmados"),
    { ...participante, origem, criadoEm: serverTimestamp() }
  );
  return ref.id;
}

export async function removerConfirmado(edicaoId: string, confirmadoId: string): Promise<void> {
  await deleteDoc(doc(db, "edicoes", edicaoId, "confirmados", confirmadoId));
}

/** Move um participante da lista de espera para confirmados */
export async function confirmarDaEspera(
  edicaoId: string,
  participante: Participante
): Promise<void> {
  const batch = writeBatch(db);

  const confirmadoRef = doc(collection(db, "edicoes", edicaoId, "confirmados"));
  batch.set(confirmadoRef, {
    nomeCompleto: participante.nomeCompleto,
    cpf: participante.cpf,
    email: participante.email,
    whatsapp: participante.whatsapp,
    origem: "listaEspera" as OrigemConfirmado,
    criadoEm: serverTimestamp(),
  });

  const esperaRef = doc(db, "edicoes", edicaoId, "listaEspera", participante.id);
  batch.delete(esperaRef);

  await batch.commit();
}

/** Importa múltiplos confirmados de uma vez (Excel) */
export async function importarConfirmados(
  edicaoId: string,
  participantes: ParticipanteForm[]
): Promise<void> {
  const LOTE = 400;
  for (let i = 0; i < participantes.length; i += LOTE) {
    const batch = writeBatch(db);
    participantes.slice(i, i + LOTE).forEach((p) => {
      const ref = doc(collection(db, "edicoes", edicaoId, "confirmados"));
      batch.set(ref, { ...p, origem: "excel", criadoEm: serverTimestamp() });
    });
    await batch.commit();
  }
}
